import React, { Component } from 'react';
import classnames from 'classnames';
import idx from 'idx';
import partial from 'lodash/partial';
import capitalize from 'lodash/capitalize';
import { CheckBoxField, TextAreaField } from 'common/components/form_fields';
import { getDetailedAwardCost, isDiscountFeature } from 'pap/utils';
import RcSlider from 'rc-slider';
import Slider from 'common/components/slider';
import Money from 'common/components/money';
import { MIN_AWARD_STEP, DEFAULT_MAX_AWARD } from 'pap/constants';

/*
 * Simple feature price component to be used across package fields.
*/
const FeaturePrice = ({ feature, isDiscount, recommended }) => {
  const price = parseFloat(feature.price)
  const normalPrice = parseFloat(feature.normal_price || 0)
  const showFree = price == 0 || feature.included || isDiscount
  const showNormalPrice = normalPrice != 0 && feature.included && !isDiscount

  return (
    <div className={"price " + (showNormalPrice ? "with-normal-price": "")}>

      { recommended &&
      <div>
        <span className="recommended hide-for-small-only">Recommended </span>
        <span className="hide-for-small-only">(</span>
        FREE
        <span className="hide-for-small-only">)</span>
      </div> }

      { showFree && !recommended && <div className="free">free</div> }

      { !showFree && !recommended && <div>+<Money value={feature.price}/></div> }

      {showNormalPrice &&
      <div className="normal-price">+<Money value={feature.normal_price}/></div>}
    </div>
  )
}


/*
 * Single (boolean) package feature field.
 *
 * @author: Nathan Ozelim <nathan@startupfoundry.com>
*/
const SingleFeatureField = ({
  fieldName, name, features, stepValues, onChange, disclaimer, recommended, downgradeForbidden,
  projectFields, projectData, billables, user, displayName, extraComponent
}) => {

  const Extra = extraComponent;

  let feature = (features || []).find((f) => f.feature.name == name)

  if (!feature) {
    return null
  }

  const values = stepValues[fieldName]
  const isDiscount = isDiscountFeature(feature, features, billables, user)
  feature = feature.feature
  const selected = (
    isDiscount || (values === undefined ? feature.active : values)
  )
  const price = isDiscount? 0 : parseFloat(feature.price)
  const classes = classnames('form-row', {
    'selected': selected,
    'included': feature.included,
    'free': price == 0
  })

  return (
    <div className="pap-item-box row">
      <div className="columns large-2 show-for-large icon-container">
        <i className={feature.icon || "fa fa-info"} />
      </div>
      <div className="columns large-14 small-16 content-container">
        <div className={classes}>
          <FeaturePrice feature={feature} isDiscount={isDiscount} recommended={recommended}/>

          {feature.included && <div className="included-badge">Included</div>}

          <CheckBoxField meta={{}} label={displayName || capitalize(feature.name)}
            disabled={feature.read_only || isDiscount || (feature.active && downgradeForbidden)}
            input={{
              value: selected,
              name: fieldName,
              onChange: partial(onChange, fieldName)
            }}
            htmlDescription={feature.description}
            disclaimer={disclaimer}/>

          { !!Extra &&
          <div className="extra-wrapper">
            <Extra featureSelected={selected} stepValues={stepValues} project={projectData} />
          </div> }

          {/* project fields that can be changed once this feature is active */}
          {selected && projectFields.map((field, i) => {
            const show = field.shouldShow? field.shouldShow(feature) : true;
            if (!show) {
              return false;
            }

            /* value is determined according to the following precedence order:
               form (dirty) value -> project field value -> default value */
            let value = field.defaultValue;
            if (stepValues[field.name] !== undefined) {
              value = stepValues[field.name];
            } else if (projectData) {
              if (field.type == 'brief_question') {
                value = projectData.brief_answers[field.name];
              } else {
                value = projectData[field.name];
              }
            }

            const disabled = feature.active && downgradeForbidden;

            return (
              <div className="dependant-field" key={i}>
                {field.type == 'bool' &&
                <CheckBoxField meta={{}} label={field.label} disabled={disabled}
                  input={{
                    value: value,
                    name: field.name,
                    onChange: partial(onChange, field.name)
                  }}
                  description={field.description}/>}

                {field.type == "brief_question" &&
                <TextAreaField meta={{}} label={field.label} disabled={disabled}
                  input={{
                    value: value,
                    name: field.name,
                    onChange: e => onChange(field.name, e.target.value)
                  }}
                  rows={4}
                  minCharCount={field.minCharCount}
                  description={field.description}/>}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  );
}

/*
 * Group package feature field.
 *
 * There are 2 possible display formats for a group of features:
 *
 * With `groupLabel` argument specified
 * ------------------------------------
 * X Group Label
 *   X non-included feature 1
 *   X non-included feature 2
 *   ...
 *   X non-included feature N
 *
 * In this case, an included feature is always expected, so the group will always have
 * one feature active.
 *
 * Without `groupLabel` argument specified
 * ---------------------------------------
 * X non-included feature 1
 * X non-included feature 2
 * ...
 * X included feature
 * ...
 * X non-included feature N
 *
 * In this case, a included feature is not always expected, so it's possible that the
 * group will have no feature active at a given time.
 *
 * In order to represent this distinction we can have the following field states:
 * `stepValues[fieldName]` === undefined (field is in pristine state)
 * `stepValues[fieldName]` === null (no feature for this group will be active)
 * `stepValues[fieldName]` === {ID} (feature with id {ID} will be active)
 *
 * @author: Nathan Ozelim <nathan@startupfoundry.com>
*/
const GroupFeatureField = ({
  fieldName, name, features, stepValues, onChange, getGroupDescription, groupLabel,
  downgradeForbidden, billables, user
}) => {

  if (!features) {
    return null;
  }

  const groupFeatures = features.filter((f) => f.feature.group == name).map((f) => f.feature)
  const includedFeature = groupFeatures.find((f) => f.included)
  const activeFeature = groupFeatures.find((f) => f.active)
  const nonIncludedFeatures = groupFeatures.filter((f) => !f.included)
  const discountFeature = groupFeatures.find((f) => (
    isDiscountFeature({feature: f}, features, billables, user)
  ))

  if (groupLabel) {
    if (!includedFeature) {
      return null;
    }

    const selected = (
      !!discountFeature || (
      stepValues[fieldName] === undefined?
      !includedFeature.active : stepValues[fieldName] != includedFeature.id )
    )

    return (
      <div className="pap-item-box row">
        <div className="columns large-2 show-for-large icon-container">
          <i className={includedFeature.icon || "fa fa-info"} />
        </div>
        <div className="columns large-14 small-16 content-container">
          <div className={classnames('form-row', { selected })}>
            <CheckBoxField meta={{}} label={capitalize(groupLabel)}
              disabled={!!discountFeature || (downgradeForbidden && !includedFeature.active)}
              input={{
                value: selected,
                name: fieldName,
                onChange: (v) => onChange(fieldName, v? nonIncludedFeatures[0].id:includedFeature.id)
              }}
              description={getGroupDescription(includedFeature.value)}/>

            <div className="group-choices">
              {nonIncludedFeatures.map((f, i) => {
                const isDiscount = discountFeature && discountFeature.id === f.id
                const val = (
                  isDiscount ||
                  (stepValues[fieldName] === undefined? f.active:stepValues[fieldName] == f.id)
                )

                const disabled = (
                  !!discountFeature ||
                  (downgradeForbidden &&
                  (val === true || parseFloat(f.price) < parseFloat(activeFeature.price))
                ))

                return (
                  <div className="group-choice" key={i}>
                    <div className="price">
                      {isDiscount? 'FREE': <span>+<Money value={f.price}/></span>}
                    </div>
                    <CheckBoxField key={f.id} meta={{}} label={`${f.name}`}
                      disabled={disabled}
                      input={{
                        value: val,
                        name: `${fieldName}_${f.id}`,
                        onChange: (v) => onChange(fieldName, v? f.id:includedFeature.id)
                      }} />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group-features">
      {groupFeatures.map((f, i) => {
        const isDiscount = discountFeature && discountFeature.id === f.id
        const val = (
          isDiscount ||
          (stepValues[fieldName] === undefined?
           (f.active && !discountFeature) : stepValues[fieldName] == f.id)
        )

        const disabled = (
          !!discountFeature ||
          (downgradeForbidden &&
          (val === true || (activeFeature &&
          parseFloat(f.price) < parseFloat(activeFeature.price))))
        )

        return (
          <div className="pap-item-box row" key={i}>
            <div className="columns large-2 show-for-large icon-container">
              <i className={f.icon || "fa fa-info"} />
            </div>
            <div className="columns large-14 small-16 content-container">
              <div className={classnames('form-row feature', {
                'included': f.included,
                'selected': val
              })}>
                <FeaturePrice feature={f} isDiscount={isDiscount}/>

                {f.included && <div className="included-badge">Included</div>}

                <CheckBoxField meta={{}} label={capitalize(f.name)}
                  disabled={disabled}
                  input={{
                    value: val,
                    name: `${fieldName}_${f.id}`,
                    onChange: (v) => onChange(fieldName,
                                              v? f.id:(includedFeature? includedFeature.id:null))
                  }}
                  htmlDescription={f.description} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/*
 * Field to manipulate included awards, i.e. those that are already part of the package.
 *
 * @author: Nathan Ozelim <nathan@startupfoundry.com>
*/
const IncludedAwardsField = ({ fieldName, awards, stepValues, onChange, downgradeForbidden,
  awardedCreatives }) => {

  const includedAwards = awards.filter(({feature}) => feature.included);
  const firstAwardValue = parseInt(idx(includedAwards, _ => _[0].feature.value) || 0);
  const values = stepValues[fieldName];

  /* get total cost variation */
  let costVariation = 0
  includedAwards.forEach(a => {
    const award = a.feature;
    const value = (values || {})[award.id] || award.value;
    const detailedCost = getDetailedAwardCost(award, value);
    costVariation = costVariation + detailedCost.totalVariation;
  })

  if (includedAwards.length === 0) {
    return null;
  }

  return (
    <div className="pap-item-box row">
      <div className="columns large-2 show-for-large icon-container">
        <span className="fa-stack">
          <i className="fa fa-circle-thin fa-stack-2x" />
          <i className="fa fa-usd fa-stack-1x" />
        </span>
      </div>
      <div className="columns large-14 small-16 content-container">
        <div className={"form-row " + (values ? "selected":"")}>
          <div className="price">
            +<Money value={costVariation}/>
          </div>

          <CheckBoxField meta={{}} label="Increase the award"
            input={{
              value: !!values,
              name: fieldName,
              onChange: (v) => onChange(fieldName, v? {}:null)
            }}
            description={
              `The package you selected includes a $${firstAwardValue} award.
              By increasing the award, you will encourage more creatives to participate and receive
              more entries.`
            } />

          <div className="sliders">
            {includedAwards.map((a, i) => {
              const award = a.feature;
              const value = (values || {})[award.id] || award.value;
              const min = downgradeForbidden? award.value : award.min;
              const wrapupComplete = !!awardedCreatives.find(aw => (
                aw.award_uuid === a.uuid && aw.wrapup_complete
              ));
              const fees = value? getDetailedAwardCost(award, value).fees : 0;

              return (
                <div key={i}>
                  <h4 className="award-description">
                    <Money value={value} showDecimals={false}/>
                    <span className="hide-for-small-only"> award</span>
                    {' '}(+ <Money value={fees} /> fee)
                  </h4>

                  <RcSlider min={parseFloat(min) || 0}
                    max={award.max || DEFAULT_MAX_AWARD}
                    step={MIN_AWARD_STEP}
                    disabled={wrapupComplete || !values}
                    onChange={(v) => onChange(fieldName, {...values, [award.id]: v})}
                    value={parseFloat(value)}/>
                </div>)
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * Field to manipulate extra awards, i.e. those that are not part of the package.
 *
 * @author: Nathan Ozelim <nathan@startupfoundry.com>
*/
class ExtraAwardsField extends Component {
  constructor(props) {
    super(props)

    this.clearAll = this.clearAll.bind(this)
    this.getExtraAwards = this.getExtraAwards.bind(this)
    this.getReferenceAward = this.getReferenceAward.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.remove = this.remove.bind(this)
  }

  clearAll() {
    /* clear all awards not yet submitted and mark all existing ones as deleted */
    const extraAwards = this.getExtraAwards()
    const newValues = []
    extraAwards.map((a) => newValues.push({uuid: a.uuid, deleted: true}))
    this.props.onChange(this.props.fieldName, newValues)
  }

  getExtraAwards() {
    return this.props.awards.filter(
      ({feature}) => !feature.included && feature.active
    )
  }

  getReferenceAward() {
    const referenceAward = this.props.awards.find((a) => a.feature.position == 0)
    return referenceAward? referenceAward.feature : null
  }

  handleChange(updates, idx) {
    const {fieldName, onChange, stepValues} = this.props
    const newValues = [...(stepValues[fieldName] || [])]
    /* update position if idx is specified, otherwise append to the end */
    const pos = idx != -1? idx:(newValues.length)
    newValues[pos] = {...newValues[pos], ...updates}
    onChange(fieldName, newValues)
  }

  remove(idx, uuid) {
    /* if award exists, mark it as deleted */
    if (uuid) {
      this.handleChange({uuid, deleted: true}, idx)
      return
    }
    /* otherwise, just remove from the list */
    const {fieldName, onChange, stepValues} = this.props
    const newValues = [...(stepValues[fieldName] || [])]
    newValues.splice(idx, 1)
    onChange(fieldName, newValues)
  }

  renderAward(value, idx, uuid, is1to1) {
    const { awards, awardedCreatives, downgradeForbidden } = this.props;

    const award = (
      uuid? awards.find((a) => a.uuid == uuid).feature : this.getReferenceAward()
    );
    const wrapupComplete = (
      uuid && !!awardedCreatives.find(aw => aw.award_uuid === uuid && aw.wrapup_complete)
    );
    const fees = value? getDetailedAwardCost(award, value).fees : 0;
    const min = parseFloat(downgradeForbidden? award.value : award.min) || 0;

    if (is1to1) {
      return (
        <div key={uuid || idx}>
          <Slider min={min}
            max={award.max || DEFAULT_MAX_AWARD}
            step={MIN_AWARD_STEP}
            disabled={wrapupComplete}
            onChange={value => this.handleChange({ uuid, value }, idx)}
            value={parseFloat(value || 0)}/>
        </div>
      );
    }

    return (
      <div className="extra-award-container" key={uuid || idx}>
        <div className="row">
          <div className="columns small-13 medium-14">
            <h4 className="award-description">
              <span className="hide-for-small-only">Extra </span>
              <Money value={value} showDecimals={false}/>
              <span className="hide-for-small-only"> award</span>
              {' '}(+ <Money value={fees} /> fee)
            </h4>
            <RcSlider
              min={min}
              max={award.max || DEFAULT_MAX_AWARD}
              step={MIN_AWARD_STEP}
              disabled={wrapupComplete}
              onChange={value => this.handleChange({ uuid, value }, idx)}
              value={parseFloat(value || 0)} />
          </div>

          { (!uuid || !downgradeForbidden) &&
          <div className="columns small-3 medium-2">
            <i className="fa fa-times-circle pull-right" onClick={() => this.remove(idx, uuid)}></i>
          </div> }
        </div>
      </div>
    );
  }

  render() {
    const { fieldName, stepValues, onChange, error, projectData, awards } = this.props;
    /* 1-to-1 projects have just one extra award that can only be upgraded */
    const is1to1 = projectData && projectData.is_one_to_one;
    const referenceAward = this.getReferenceAward();
    const values = stepValues[fieldName] || [];
    const extraAwards = this.getExtraAwards();
    const includedAwards = awards.filter(({feature}) => feature.included);
    const firstAwardValue = parseInt(idx(includedAwards, _ => _[0].feature.value) || 0);

    /* if no reference award is present, it means that extra awards can't be created */
    if (!referenceAward) {
      return null
    }

    /* get total cost variation */
    let total = 0
    let totalVariation = 0;
    /* extra awards already created */
    extraAwards.map((a) => {
      /* check if modified */
      const edited = values.find((v) => v.uuid == a.uuid)

      /* marked as deleted: ignore */
      if (edited && edited.deleted) {
        return
      }
      const value = edited? edited.value : a.feature.value
      const detailedCost = getDetailedAwardCost(a.feature, value)
      total = total + detailedCost.total
      totalVariation = totalVariation + detailedCost.totalVariation
    })

    /* extra awards still not submitted */
    values.filter((v) => !v.uuid && v.value).map((v) => {
      /* as the award hasn't yet been created, we need to use the reference award to calculate
         its cost */
      const detailedCost = getDetailedAwardCost(referenceAward, v.value)
      total = total + detailedCost.total
      totalVariation = totalVariation + detailedCost.totalVariation
    })
    const enabled = total > 0 || values.filter(v => !v.deleted).length > 0

    return (
      <div className="pap-item-box row">
        <div className="columns large-2 show-for-large icon-container">
          <span className="fa-stack">
            <i className="fa fa-circle-thin fa-stack-2x" />
            <i className="fa fa-plus fa-stack-1x" />
          </span>
        </div>
        <div className="columns large-14 small-16 content-container">
          <div className={classnames('form-row', { 'selected': enabled })}>
            <div className="price">
              <span>+<Money value={is1to1? totalVariation : total}/></span>
            </div>

            { is1to1? <h4>Increase award:</h4> :
            <CheckBoxField meta={{}} label="Add more awards"
              input={{
                value: enabled,
                name: fieldName,
                onChange: (v) => (
                  v? onChange(fieldName, [{ value: referenceAward.min }]) : this.clearAll()
                )
              }}
              description={`The package you selected includes one $${firstAwardValue} award.
                Multiple awards increase participation and let you combine elements
                or ideas from different entries. Each award comes with its own legal
                contract and deliverables.`}/> }

            <div className={is1to1? "" : "extra-awards"}>
              {/* extra awards already created */}
              {extraAwards.map((award, i) => {
                const editedIdx = values.findIndex((v) => v.uuid == award.uuid)
                const edited = values[editedIdx]

                /* marked as deleted: ignore */
                if (edited && edited.deleted) {
                  return null
                }

                const value = edited? edited.value : award.feature.value

                return this.renderAward(value, editedIdx, award.uuid, is1to1)
              })}

              {/* extra awards still not submitted */}
              { !is1to1 && values.map((v, i) => {
                if (v.uuid) {
                  return null
                }
                return this.renderAward(v.value, i)
              }) }

              { !is1to1 && enabled &&
              <button className="btn btn-md btn-text btn-add"
                  onClick={(e) => {
                    e.preventDefault();
                    this.handleChange({ value: referenceAward.min }, -1);
                  }}>
                <i className="fa fa-plus-circle"/> Add another award
              </button> }

              { !is1to1 && error && error.extra_awards &&
              <div>
                { error.extra_awards.filter((err) => err.value).map((err, i) => (
                  <div key={i} className="callout warning">{err.description}</div>
                )) }
              </div> }
            </div>
          </div>
        </div>
      </div>
    );
  }
}


export { SingleFeatureField, GroupFeatureField, IncludedAwardsField, ExtraAwardsField };
